import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook } from 'lucide-react';
import { WhatsappLogo, TiktokLogo } from '@phosphor-icons/react';

const Footer = () => {
  return (
    <footer className="bg-brand-black border-t border-brand-cobalt/15 text-white mt-auto w-full" data-testid="footer">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-12 py-10 md:py-16">
        {/* Main Grid - 4 columns on desktop, stacked on mobile */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6">
          
          {/* Column 1: Alphatica */}
          <div className="col-span-2 md:col-span-1 text-center md:text-left mb-4 md:mb-0">
            <Link to="/" className="inline-block mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_alphatica-store/artifacts/vng9vv17_logo-alphatica-blanco.png"
                alt="Alphatica"
                className="h-10 md:h-12 w-auto mx-auto md:mx-0"
              />
            </Link>
            <p className="text-xs text-white/50 leading-relaxed max-w-xs mx-auto md:mx-0 mb-4">
              Fragancias de lujo inspiradas en las mejores marcas del mundo.
            </p>
            {/* Social Icons */}
            <div className="flex justify-center md:justify-start space-x-4">
              <a href="https://instagram.com/Alphatica_mx" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-brand-magenta transition-colors" aria-label="Instagram">
                <Instagram size={20} />
              </a>
              <a href="https://www.facebook.com/alphatica.mx" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-brand-cobalt transition-colors" aria-label="Facebook">
                <Facebook size={20} />
              </a>
              <a href="https://tiktok.com/@alphaticamx" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-brand-magenta transition-colors" aria-label="TikTok">
                <TiktokLogo size={20} weight="fill" />
              </a>
              <a href="https://wa.me/524422585560?text=Hola%20me%20interesa%20informaci%C3%B3n" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-[#25D366] transition-colors" aria-label="WhatsApp">
                <WhatsappLogo size={20} weight="fill" />
              </a>
            </div>
          </div>

          {/* Column 2: Descubre */}
          <div className="text-center md:text-left">
            <h4 className="text-[10px] md:text-xs uppercase tracking-widest text-brand-cobalt mb-3 md:mb-4">
              Descubre
            </h4>
            <nav className="flex flex-col space-y-2">
              <Link to="/products" className="text-xs md:text-sm text-white/60 hover:text-brand-cobalt transition-colors">
                Catálogo de perfumes
              </Link>
              <Link to="/products?sort_by=sales_count" className="text-xs md:text-sm text-white/60 hover:text-brand-cobalt transition-colors">
                Más vendidos
              </Link>
              <Link to="/products?perfume_type=niche" className="text-xs md:text-sm text-white/60 hover:text-brand-cobalt transition-colors">
                Fragancias de nicho
              </Link>
              <Link to="/novedades" className="text-xs md:text-sm text-white/60 hover:text-brand-cobalt transition-colors">
                Nuevos lanzamientos
              </Link>
            </nav>
          </div>

          {/* Column 3: Atención al Cliente */}
          <div className="text-center md:text-left">
            <h4 className="text-[10px] md:text-xs uppercase tracking-widest text-brand-magenta mb-3 md:mb-4">
              Atención al Cliente
            </h4>
            <nav className="flex flex-col space-y-2">
              <Link to="/shipping" className="text-xs md:text-sm text-white/60 hover:text-brand-magenta transition-colors">
                Política de envíos
              </Link>
              <Link to="/returns" className="text-xs md:text-sm text-white/60 hover:text-brand-magenta transition-colors">
                Cambios y reembolsos
              </Link>
              <Link to="/order-tracking" className="text-xs md:text-sm text-white/60 hover:text-brand-magenta transition-colors">
                Seguimiento de pedido
              </Link>
              <Link to="/faq" className="text-xs md:text-sm text-white/60 hover:text-brand-magenta transition-colors">
                Preguntas frecuentes
              </Link>
            </nav>
          </div>

          {/* Column 4: Alphatica Info */}
          <div className="text-center md:text-left">
            <h4 className="text-[10px] md:text-xs uppercase tracking-widest text-brand-cobalt mb-3 md:mb-4">
              Alphatica
            </h4>
            <nav className="flex flex-col space-y-2">
              <Link to="/about" className="text-xs md:text-sm text-white/60 hover:text-brand-cobalt transition-colors">
                Sobre Alphatica
              </Link>
              <Link to="/contact" className="text-xs md:text-sm text-white/60 hover:text-brand-cobalt transition-colors">
                Contacto
              </Link>
            </nav>
          </div>
        </div>

        {/* Legal Links */}
        <div className="border-t border-brand-cobalt/10 mt-8 pt-6">
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mb-4">
            <Link to="/privacy" className="text-[10px] md:text-xs text-white/40 hover:text-brand-cobalt transition-colors">
              Aviso de Privacidad
            </Link>
            <span className="text-brand-cobalt/20 hidden md:inline">|</span>
            <Link to="/terms" className="text-[10px] md:text-xs text-white/40 hover:text-brand-cobalt transition-colors">
              Términos y Condiciones
            </Link>
            <span className="text-brand-cobalt/20 hidden md:inline">|</span>
            <Link to="/cookies" className="text-[10px] md:text-xs text-white/40 hover:text-brand-cobalt transition-colors">
              Política de Cookies
            </Link>
            <span className="text-brand-cobalt/20 hidden md:inline">|</span>
            <Link to="/disclaimer" className="text-[10px] md:text-xs text-white/40 hover:text-brand-cobalt transition-colors">
              Disclaimer Legal
            </Link>
          </div>
          <p className="text-[10px] text-white/30 uppercase tracking-wider text-center">
            © 2025 Alphatica. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
